import clsx from 'clsx';
import {
    BuildingLibraryIcon,
    ChevronDownIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    UserIcon,
} from '@heroicons/react/24/outline';
import type { ClientVacationGroup } from '../../../models/vacationClientView.types';
import {
    HEADER_OVERLAP_PILL_CLASS,
    HEADER_PENDING_PILL_CLASS,
    HEADER_PEOPLE_PILL_CLASS,
    HEADER_PILL_HINTS,
    HEADER_PILL_LABELS,
} from '../vacationClientPanel.definitions';

export interface ClientPanelHeaderProps {
    group: ClientVacationGroup;
    isOpen: boolean;
    controlsId: string;
    onToggle: () => void;
}

export function ClientPanelHeader({ group, isOpen, controlsId, onToggle }: ClientPanelHeaderProps) {
    return (
        <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={controlsId}
            data-tour="conflicts-client-header"
            onClick={onToggle}
            className="flex w-full items-center justify-between gap-3 flex-wrap bg-raro-gray-50 px-4 py-3 text-left hover:bg-raro-gray-100 transition-colors"
        >
            <span className="flex items-center gap-2 min-w-0">
                <BuildingLibraryIcon className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                <span className="truncate text-sm font-semibold text-raro-gray-900 font-montserrat">
                    {group.clientName}
                </span>
            </span>
            <span className="flex items-center gap-2">
                <span
                    title={HEADER_PILL_HINTS.people}
                    className={clsx(
                        'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold font-montserrat',
                        HEADER_PEOPLE_PILL_CLASS
                    )}
                >
                    <UserIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {HEADER_PILL_LABELS.people(group.totalPeople)}
                </span>
                {group.pendingCount > 0 && (
                    <span
                        title={HEADER_PILL_HINTS.pending}
                        className={clsx(
                            'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold font-montserrat',
                            HEADER_PENDING_PILL_CLASS
                        )}
                    >
                        <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {HEADER_PILL_LABELS.pending(group.pendingCount)}
                    </span>
                )}
                {group.overlapCount > 0 && (
                    <span
                        title={HEADER_PILL_HINTS.overlap}
                        className={clsx(
                            'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold font-montserrat',
                            HEADER_OVERLAP_PILL_CLASS
                        )}
                    >
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {HEADER_PILL_LABELS.overlap(group.overlapCount)}
                    </span>
                )}
                <ChevronDownIcon
                    className={clsx('h-4 w-4 shrink-0 text-raro-gray-400 transition-transform', isOpen && 'rotate-180')}
                    aria-hidden="true"
                />
            </span>
        </button>
    );
}

export default ClientPanelHeader;
